'use client';

import { useLocale } from '@/components/providers/context/locale-context';
import useWindowSize from '@/lib/hooks/use-windows-size';
import clsx from 'clsx';
import { throttle } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { JContainer } from '@/components/ui/layout';

function processTextSpan(
    textSpan: HTMLSpanElement,
    startProcess: number,
    endProcess: number,
    currentProcess: number,
) {
    if (currentProcess > startProcess && currentProcess < endProcess) {
        const currentDistance = currentProcess - startProcess;

        const totalDistance = endProcess - startProcess;

        const progress = currentDistance / totalDistance;

        textSpan.style.opacity = `${0.2 + progress}`;
    } else if (currentProcess <= startProcess) {
        textSpan.style.opacity = '0.2';
    } else if (currentProcess >= endProcess) {
        textSpan.style.opacity = '1';
    }
}

function TeamDesc() {
    const locale = useLocale();
    const textSpanContainerRef = useRef<HTMLDivElement>(null);
    const textSpanRefs = useRef<(HTMLSpanElement | null)[]>([]);
    const { height } = useWindowSize();

    const { t } = useTranslation('team');

    const descs: string[] = t('descs', { returnObjects: true }) as string[];

    const stepDistance = 1 / descs.length;

    const handleScroll = useCallback(() => {
        if (!textSpanContainerRef.current || height == 0) {
            return;
        }

        const { top, bottom } =
            textSpanContainerRef.current.getBoundingClientRect();
        // 检查组件是否在视口中
        if (bottom < 0 || top > window.innerHeight) {
            return; // 组件不在屏幕中，忽略处理
        }

        const distance = height - bottom;
        handleTextSpans(distance);
    }, [textSpanContainerRef.current, height]);

    useEffect(() => {
        // 监听滚动事件
        window.addEventListener('scroll', handleScroll);

        // 组件卸载时移除事件监听
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []); // 注意这里的依赖数组是空的，这意味着事件监听只会在组件挂载时添加一次

    const calculateProcess = (distance: number) => {
        if (distance <= 50) return 0;
        if (distance >= 250) return 1;
        return distance / 250;
    };

    const handleTextSpans = throttle((distance: number) => {
        if (textSpanRefs.current.length !== descs.length) {
            return;
        }

        const currentProcess = calculateProcess(distance);

        textSpanRefs.current.forEach((textSpan, index) => {
            if (textSpan) {
                processTextSpan(
                    textSpan,
                    stepDistance * index,
                    stepDistance * (index + 1),
                    currentProcess,
                );
            }
        });
    }, 100);

    return (
        <div className="flex w-full flex-col items-center text-white">
            <div className={clsx('j-h2')}>{t('descTitle')}</div>
            <JContainer
                ref={textSpanContainerRef}
                size="lg"
                padding="none"
                className={clsx(
                    'j-h5 mb-[80px] mt-[24px] px-[37.5px] text-center sm:mb-[160px]',
                )}
            >
                {descs.map((text, index) => (
                    <span
                        key={'desc-span-' + index}
                        ref={(el) => {
                            // 确保el不为null再赋值
                            if (el) textSpanRefs.current[index] = el;
                        }}
                    >
                        {text}
                        <br />
                    </span>
                ))}
            </JContainer>
        </div>
    );
}

export default TeamDesc;
